<template #default>
    <form
        v-if="status.detail"
        @submit.prevent="panelUUID ? requestUpdate() : requestCreate()"
    >
        <b-form-group
            :label="$t('label.name')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.name"
                :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
                autocomplete="off"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">
                {{ $t("message.requiredX", [$t("label.name")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.phone')"
            label-cols="4"
            label-align="left"
        >
            <vue-tel-input
                v-on:country-changed="(country) => phoneCountryChanged(country)"
                v-model.trim="createForm.phone"
                :class="{ 'border border-danger': submitted && !$v.createForm.phone.required }"
                :defaultCountry="createForm.phoneIso || 'MY'"
                :autoDefaultCountry="false"
                :autoFormat="false"
                autocomplete="off"
            />
            <div v-if="submitted && !$v.createForm.phone.required" class="invalid-feedback d-block text-right">
                {{ $t("message.requiredX", [$t("label.phone")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.email')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.email"
                :class="{ 'is-invalid': submitted && $v.createForm.email.$error }"
                autocomplete="off"
            />
            <b-form-invalid-feedback v-if="submitted && $v.createForm.email.$error" class="text-right">
                <span v-if="!$v.createForm.email.required">{{ $t("message.requiredX", [$t("label.email")]) }}</span>
                <span v-else-if="!$v.createForm.email.email">{{ $t("message.invalidX", [$t("label.email")]) }}</span>
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('module.panelType')"
            label-cols="4"
            label-align="left"
        >
            <multiselect
                v-model="createForm.panelType"
                deselect-label="Can't remove this value"
                track-by="value"
                label="text"
                :placeholder="$t('message.pleaseSelectX', [])"
                :options="panelTypeDdl"
                :searchable="true"
                :allow-empty="false"
                :append-to-body="true"
                :class="[submitted && !$v.createForm.panelType.required ? 'border border-danger' : '']"
            />
            <div v-if="submitted && !$v.createForm.panelType.required" class="invalid-feedback d-block text-right">
                {{ $t("message.requiredX", [$t("module.panelType")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('module.location')"
            label-cols="4"
            label-align="left"
        >
            <multiselect
                v-model="createForm.location"
                deselect-label="Can't remove this value"
                track-by="value"
                label="text"
                :placeholder="$t('message.pleaseSelectX', [])"
                :options="locationDdl"
                :searchable="true"
                :allow-empty="false"
                :append-to-body="true"
                :class="[submitted && !$v.createForm.location.required ? 'border border-danger' : '']"
            />
            <div v-if="submitted && !$v.createForm.location.required" class="invalid-feedback d-block text-right">
                {{ $t("message.requiredX", [$t("module.location")]) }}
            </div>
        </b-form-group>
        <b-form-group
            label="Address"
            label-cols="4"
            label-align="left"
        >
            <b-form-textarea
                v-model.trim="createForm.address"
                autocomplete="off"
            />
        </b-form-group>
        <b-form-group
            label="Map Link"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.mapLink"
                autocomplete="off"
            />
        </b-form-group>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ panelUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import { VueTelInput } from 'vue-tel-input';
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { panelMethods, lookupMethods } from "@/state/api";
import { PANEL_MODULE } from "@/state/api/panel.state";

export default {
    props: {
        panelUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                name: { required },
                phone: { required },
                email: { required, email },
                panelType: { required },
                location: { required },
            },
        };
    },
    components: { Multiselect, VueTelInput },
    async created() {
        await this.lookupPanelType({});
        await this.lookupLocation({});
        await this.getPanelData();
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
        };
    },
    methods: {
        ...panelMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: "",
                name: "",
                phone: "",
                phoneCode: "",
                phoneIso: "",
                email: "",
                address: "",
                mapLink: "",
                panelType: null,
                location: null,
            };
        },
        async getPanelData() {
            if (this.panelUUID) {
                await this.getPanel({ uuid: this.panelUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                name: this.data.name,
                                phone: this.data.phone,
                                phoneCode: this.data.phoneCode,
                                phoneIso: "",
                                email: this.data.email,
                                address: this.data.address,
                                mapLink: this.data.mapLink,
                                panelType: this.data.panelType ? {
                                    value: this.data.panelType.uuid,
                                    text: this.data.panelType.name,
                                } : null,
                                location: this.data.location ? {
                                    value: this.data.location.uuid,
                                    text: this.data.location.name,
                                } : null,
                            };
                        }
                    });
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    panelTypeUUID: this.createForm.panelType.value,
                    locationUUID: this.createForm.location.value,
                };
                this.createPanel({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    panelTypeUUID: this.createForm.panelType.value,
                    locationUUID: this.createForm.location.value,
                };
                this.updatePanel({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        phoneCountryChanged(country) {
            this.createForm.phoneCode = "+" + country.dialCode;
            this.createForm.phoneIso = country.iso2;
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(PANEL_MODULE, ['status']),
        panelTypeDdl() {
            return this.$store.state.api.lookup.panelTypeDdl || [];
        },
        locationDdl() {
            return this.$store.state.api.lookup.locationDdl || [];
        },
        data() {
            return this.$store.state.api.panel.details;
        },
    },
}
</script>
